// Pure helpers for the By Treatment report. Kept free of React/redux so the
// arithmetic the page shows (conversion, drop-off, chips, previous-period URL)
// is unit-testable and cannot drift from what the components render.

/** part / whole as a number rounded to 1dp (0 when whole is 0). */
export const pctOf = (part, whole) => {
    if (!whole) return 0;
    return Math.round(((part || 0) / whole) * 1000) / 10;
};

/**
 * Overall conversion = Completed (CLOSED) / Total Enquiries.
 * Drop-off = Enquiries - Completed (never negative).
 * Side outcomes (Failed to Attend, Archived) must never feed this.
 */
export const overallConversion = (enquiries, completed) => ({
    pct: pctOf(completed, enquiries),
    dropOff: Math.max((enquiries || 0) - (completed || 0), 0),
});

/** Sentinel PracticeTreatment id that matches no lead (an empty scope must match NOTHING). */
export const NO_TREATMENT_MATCH = '-1';

/**
 * PracticeTreatment ids belonging to a category. 'All' → null (= no filter). A real
 * category with no treatments → [] which callers MUST treat as "match nothing"
 * (zeros), never as "no filter".
 */
export const treatmentIdsForCategory = (practiceTreatments, category) => {
    if (!category || category === 'All') return null;
    return (practiceTreatments || [])
        .filter((t) => (t.treatment_item?.treatment_category?.name || 'Other') === category)
        .map((t) => t.id)
        .filter(Boolean);
};

/**
 * Chips for the "Treatments in Category" card: active (leads > 0) first, by lead
 * count desc then name; inactive after, by name.
 */
export const buildTreatmentChips = (treatments) => {
    const chips = (treatments || []).map((t) => ({
        id: t.id,
        name: t.name,
        leads: t.leads || 0,
        active: (t.leads || 0) > 0,
    }));
    return chips.sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        if (a.active && b.leads !== a.leads) return b.leads - a.leads;
        return String(a.name || '').localeCompare(String(b.name || ''));
    });
};

/**
 * Previous-period summary URL. When a category is active the same treatment filter
 * the current-period numbers use is applied, so deltas compare like with like.
 */
export const buildPreviousPeriodUrl = ({ practiceIds, prev, treatmentIds }) => {
    const [primary] = practiceIds;
    const pIn = practiceIds.length > 1 ? `&practice__in=${practiceIds.join(',')}` : '';
    const tFilter = treatmentIds?.length ? `&lead_treatments__treatment__in=${treatmentIds.join(',')}` : '';
    return `/api/v1/practices/${primary}/reports/leads/?aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${prev.start}&created_at__lte=${prev.end}${tFilter}${pIn}`;
};

/**
 * Union PracticeTreatment lists fetched per practice (multi-practice scope), de-duplicated
 * by PracticeTreatment id. `pages` is an array of result arrays.
 */
export const unionPracticeTreatments = (pages) => {
    const seen = new Map();
    (pages || []).forEach((results) => (results || []).forEach((t) => {
        if (t && t.id != null && !seen.has(t.id)) seen.set(t.id, t);
    }));
    return Array.from(seen.values());
};

/**
 * A selected chip only applies while it belongs to the active category tab. Derived at
 * render time (not cleared in an effect) so a tab switch never produces a render with
 * the new category + stale chip → no double funnel fetch.
 * `chip` = { id, category } captured when the chip was clicked.
 */
export const resolveEffectiveChipId = (chip, currentCategory, categoryIds) => {
    if (!chip || chip.id == null) return null;
    if (chip.category !== currentCategory) return null;
    if (Array.isArray(categoryIds) && !categoryIds.map(String).includes(String(chip.id))) return null;
    return chip.id;
};

/**
 * A chip is one PracticeTreatment (of the first practice that offers it). In a
 * multi-practice scope the same TreatmentItem has a PracticeTreatment per practice, so a
 * chip narrows to EVERY PracticeTreatment id sharing its treatment_item.
 */
export const idsForChip = (practiceTreatments, chipId) => {
    const chip = (practiceTreatments || []).find((t) => String(t.id) === String(chipId));
    const itemId = chip?.treatment_item?.id;
    if (itemId == null) return [chipId];
    return (practiceTreatments || [])
        .filter((t) => t.treatment_item?.id === itemId)
        .map((t) => t.id);
};

/** Resolve the treatment-id scope: chip > category ids > null (All). */
export const resolveScopeTreatmentIds = ({ practiceTreatments, effectiveChipId, categoryIds }) => (
    effectiveChipId != null ? idsForChip(practiceTreatments, effectiveChipId) : categoryIds
);
